import { useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface EthernetDeviceActivationTestProps {
  metroId: string;
  close: any;
}

interface EthernetDeviceActivationTestResponse {
  sse_id: string;
  status: any;
}

export const EthernetDeviceActivationTest = ({ metroId, close }: EthernetDeviceActivationTestProps) => {
  const api = useApi();

  const [peerIP, setPeerIP] = useState<string>('');
  const [vLANID, setVLANID] = useState<string>('');
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [testType, setTestType] = useState<boolean>(false);

  const testPeerIP = useMutation(api.startMetroTestApiV1StartMetroTestPost, {
    onSuccess(data) {
      const resp = data.data as EthernetDeviceActivationTestResponse;
      const msg = resp.status ?? `Tested Peer IP: ${peerIP} successfully.`;
      toast.success(msg);
      setIsTesting(false);
      setTimeout(() => { close(metroId, resp.sse_id); }, 3000);
    },
    onError: (error) => {
      apiError(error);
      setIsTesting(false);
    },
  });

  const handleSubmit = () => {
    if (peerIP) {
      if(!testType) {
        testPeerIP.mutate({
          metro_id: metroId,
          peer_ip: peerIP,
        });
      } else {
        testPeerIP.mutate({
          metro_id: metroId,
          peer_mac: peerIP,
          vlan_id: Number(vLANID),
        });
      }
      setIsTesting(true);
    } else {
      toast.error('Please enter peer IP address first.');
    }
  };

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setTestType(value === 'true');
  };

  return (
    <Box pt={2}>
      <FormControl fullWidth sx={{ mb: 4 }}>
        <FormLabel id="test-type">Test types</FormLabel>
        <RadioGroup
          row
          name="router-mode"
          onChange={handleChangeMode}
          value={testType.toString()}
        >
          <FormControlLabel value="true" control={<Radio />} label="L2" />
          <FormControlLabel value="false" control={<Radio />} label="L3" />
        </RadioGroup>
      </FormControl>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <TextField
          sx={{ mb: 4 }}
          fullWidth
          label={testType? 'Peer MAC address' : 'Peer IP address'}
          variant="outlined"
          value={peerIP}
          onChange={(e) => setPeerIP(e.target.value)}
          autoFocus
        />
      </Box>
      {testType && (
        <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
          <TextField
            sx={{ mb: 4 }}
            fullWidth
            label={'VLAN ID'}
            variant="outlined"
            value={vLANID}
            onChange={(e) => setVLANID(e.target.value)}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        {isTesting && <CircularProgress sx={{ mr: 5 }}/> }
        <Button variant="outlined" onClick={handleSubmit} disabled={!peerIP || isTesting}>
          Start Test
        </Button>
      </Box>
    </Box>
  );
};
